import React, { useState, useEffect } from 'react';
import { Container, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useAuth } from './AuthContext';
import { FaCog } from 'react-icons/fa';
import AvatarImg from "./AvatarImg";
import { resolveAvatarUrl } from "./avatar";
import badge1 from '../assets/img/badges/1_badge_rabbit.png';
import badge2 from '../assets/img/badges/2_badge_deer.png';
import badge3 from '../assets/img/badges/3_badge_wolf.png';
import badge4 from '../assets/img/badges/4_badge_horse.png';
import badge5 from '../assets/img/badges/5_badge_bear.png';
import badge6 from '../assets/img/badges/6_badge_gorilla.png';
import badge7 from '../assets/img/badges/7_badge_lion.png';
import '../css/ProfilePage.css';
import CardHSPUProgressProfile from './CardHSPUProgressProfile';
import data_program from '../data/data_program';
import { useNavigate } from 'react-router-dom';
import { fetchUserProfileFromDB, fetchAllUsernamesFromDB, saveUserProfileToDB } from './dynamoUtils';
import { useCallback } from 'react';  // Import useCallback
import AvatarEditor from './AvatarEditor';
import styles from "../css/CardHSPUProgressProfile.module.css";
import logo from "../assets/img/logo_t4a_white.svg";
import ModalCongrats from "./ModalCongrats";

function ProfilePage({ avatar: currentImage, preselectedUsername, externalUser, externalLevelToOpen, externalBestUrl }) {
  const [journeyCompletion, setJourneyCompletion] = useState(0); // State for total journey completion
  const [currentBadge, setCurrentBadge] = useState(badge1); // State for current badge image
  const [badgeLevel, setBadgeLevel] = useState('Level 1'); // State for current badge level
  const [dynamicBadges, setDynamicBadges] = useState([]);
  const { user, isAuthenticated } = useAuth();
  //const [searchTerm, setSearchTerm] = useState(isAuthenticated && user.username ? user.username : 'zarrar');
  const [displayedUser, setDisplayedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState({});
  const [searchInput, setSearchInput] = useState("");  // What the user types
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]); // Store matched usernames
  const [selectedIndex, setSelectedIndex] = useState(-1); // Track highlighted item
  const [allUsers, setAllUsers] = useState([]); // Store all usernames from the database
  const urlUsername = preselectedUsername;
  const [customModal, setCustomModal] = useState(null); // reuse from CardHSPUProgressProfile
  const { showCongrats, setShowCongrats } = useAuth();

  const handleCloseCongrats = () => {
    setShowCongrats(false);
    setTimeout(() => {
      window.location.reload(); // 🔁 Trigger hard refresh
    }, 300); // Small delay so modal fade-out feels natural
  };

  const handleMeButtonClick = () => {
    if (user?.username) {
      sessionStorage.removeItem("selectedUser");
      setSearchTerm(user.username);
      setSearchInput(user.username);
      setSuggestions([]);
      navigate(`/progress/${user.username}`);
      loadUserProfile();
    }
  };

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        // Assume fetchAllUsernamesFromDB is a function that retrieves all usernames
        const users = await fetchAllUsernamesFromDB();

        // Normalize: support either ["zarrar", ...] or [{ username, displayName }, ...]
        const normalized = users.map((u) =>
          typeof u === "string"
            ? { username: u, displayName: null }
            : u
        );

        setAllUsers(normalized);
      } catch (error) {
        console.error("Error fetching user list:", error);
      }
    };
    fetchAllUsers();
  }, []);

  useEffect(() => {
    const getUser = async () => {
      if (!user?.email) return;
      try {
      } catch (error) {
        console.error("Error fetching user:", error);
      } finally {
        setLoading(false);
      }
    };

    getUser();
  }, [user]);

  useEffect(() => {
    const loadUserFavorites = async () => {
      if (isAuthenticated && user?.username) {
        const userProfile = await fetchUserProfileFromDB(user.username);
        if (userProfile && userProfile.favorites) {
          // Convert favorites array from DB into an object for easy lookups
          const userFavoritesObject = userProfile.favorites.reduce((acc, favUsername) => {
            acc[favUsername] = true;
            return acc;
          }, {});
          setFavorites(userFavoritesObject);
        } else {
          setFavorites({}); // Clear favorites if user has none
        }
      } else {
        // If user logs out, clear favorites state
        setFavorites({});
      }
    };

    loadUserFavorites();
  }, [isAuthenticated, user?.username]);

  const navigate = useNavigate();

  useEffect(() => {
    // 1) explicit URL wins
    if (urlUsername) {
      if (searchTerm !== urlUsername) setSearchTerm(urlUsername);
      return;
    }

    // 2) explicit manual selection (sessionStorage) — respect even if authed
    let storedUser;
    try {
      const raw = sessionStorage.getItem("selectedUser");
      storedUser = raw && JSON.parse(raw);
    } catch { /* ignore parse errors */ }

    if (storedUser?.username && storedUser.username !== searchTerm) {
      setSearchTerm(storedUser.username);
      return;
    }

    // 3) authenticated default (only if nothing chosen)
    if (isAuthenticated && user?.username) {
      if (!searchTerm || searchTerm === "zarrar") {
        if (searchTerm !== user.username) setSearchTerm(user.username);
      }
      return; // don't override a manual pick
    }

    // 4) guest default
    if (!searchTerm) setSearchTerm("zarrar");
  }, [urlUsername, isAuthenticated, user?.username, searchTerm]);


  const loadUserProfile = useCallback(async () => {
    const needsFullReload =
      !displayedUser?.first_upload || !displayedUser?.progressData?.length;

    if (!searchTerm) return;
    if (displayedUser?.username === searchTerm && !needsFullReload) {
      // ❗ BUT allow reloading for "zarrar" to fix guest refresh issue
      if (searchTerm !== "zarrar") return;
    }

    setLoading(true);
    try {
      let usernameToFetch = searchTerm?.trim();

      // ✅ Prevent database fetch if not signed in
      if (!isAuthenticated) {
        console.warn("🚨 Guest access detected. Checking if the user exists...");

        let guestUser = await fetchUserProfileFromDB(searchTerm);

        if (!guestUser) {
          console.warn(`⚠️ Guest user "${searchTerm}" not found.`);
          setCustomModal({
            title: "User Not Found",
            message: `We couldn't find a profile for "${searchTerm}".\n\nPlease check the link or search for another user.`,
          });
          setDisplayedUser(null); // Prevents fallback level modal
          return;
        }
        console.log("✅ Guest user data fetched:", guestUser);
        setDisplayedUser(guestUser);
        setLoading(false);
        return;
      }

      let fetchedUser;
      const isJustCreated = localStorage.getItem("justCreated") === "true";

      if (isJustCreated) {
        console.log("🆕 First sign-in detected — trying retries before showing error...");
        for (let i = 0; i < 5; i++) {
          fetchedUser = await fetchUserProfileFromDB(usernameToFetch);
          if (fetchedUser && fetchedUser.progressData?.length > 0) {
            console.log("✅ User found after retry:", fetchedUser);
            localStorage.removeItem("justCreated");
            break;
          }
          await new Promise(res => setTimeout(res, 1000));
        }
        localStorage.removeItem("justCreated"); // Clear even if it failed
      } else {
        fetchedUser = await fetchUserProfileFromDB(usernameToFetch);
      }

      console.log(`✅ Loaded profile for ${usernameToFetch}:`, fetchedUser);
      console.log("Fetched User:", fetchedUser);
      if (fetchedUser) {
        setDisplayedUser(fetchedUser);
      } else {
        // If authenticated but DB didn’t return yet, show an empty shell of the right user
        setDisplayedUser({
          username: user?.username ?? usernameToFetch,
          avatar: "none",         // UI will show the bundled alien
          progressData: [],
        });
      }

      console.log("✅ Loaded profile for", usernameToFetch, fetchedUser);
      console.log("first_upload:", fetchedUser?.first_upload);

    } catch (error) {
      console.error("Error loading user profile:", error);

      // ✅ If authentication is required, fallback to guest
      if (!isAuthenticated) {
        setDisplayedUser({
          username: "Guest",
          avatar: "none",
          progressData: [],
        });
      }

    } finally {
      setLoading(false);
    }
  }, [searchTerm, displayedUser, isAuthenticated, user?.username]);


  const handleForceReload = () => {
    loadUserProfile();
  };

  useEffect(() => {
    console.warn(`🔄 searchTerm changed: ${searchTerm}, loading profile...`);
    loadUserProfile(); // ✅ Ensure the function runs when searchTerm updates
    // eslint-disable-next-line
  }, [searchTerm]); // ✅ This will always re-run when searchTerm changes

  useEffect(() => {
    if (!currentImage) {
      // Handle fallback logic if required
    }
  }, [currentImage]);

  useEffect(() => {
    if (!displayedUser) return;

    // ✅ Use DynamoDB value directly
    if (typeof displayedUser.journey_percentage === "number") {
      setJourneyCompletion(Math.floor(displayedUser.journey_percentage || 0));
    }

    const userProgressData = displayedUser.progressData || [];

    const updateBadge = () => {
      const badgeImages = [badge1, badge2, badge3, badge4, badge5, badge6, badge7];
      let firstIncompleteLevel = -1;

      for (let level = 0; level < 7; level++) {
        const levelTasks = data_program.filter((data) => data.level === `Level ${level + 1}`);

        const levelComplete = levelTasks.every((data) => {
          const userProgress = userProgressData.find(
            (item) => item.progress_code === data.progress_code
          );

          const progressPercentage = userProgress
            ? Math.min((userProgress.best / data.goal) * 100, 100)
            : 0;

          return progressPercentage === 100; // Ensure all sublevels are fully complete
        });

        if (!levelComplete) {
          firstIncompleteLevel = level;
          break;
        }
      }

      // Handle case where all levels are complete
      if (firstIncompleteLevel === -1) {
        firstIncompleteLevel = 6; // Set to the highest level
      }

      setCurrentBadge(badgeImages[firstIncompleteLevel]); // Use the correct badge image
      setBadgeLevel(`Level ${firstIncompleteLevel + 1}`); // Update badge level

      // Dynamically update badges
      const updatedBadges = badgeImages.map((image, index) => ({
        level: `Level ${index + 1}`,
        image,
        unlocked: index <= firstIncompleteLevel,
      }));
      setDynamicBadges(updatedBadges); // New state to hold updated badges
    };

    updateBadge();
  }, [displayedUser]);

  const getProgressBarColor = (percentage) => {
    if (percentage < 25) return '#E74C3C';
    if (percentage >= 25 && percentage < 50) return '#E67E22';
    if (percentage >= 50 && percentage < 75) return '#F1C40F';
    if (percentage >= 75 && percentage < 100) return '#3498DB';
    return '#2ECC71';
  };

  const calculateDurationDisplay = (firstUpload, completedDate, latestUpdate, progressData = []) => {
    console.log("⏱️ Raw firstUpload:", firstUpload);
    console.log("📦 completedDate:", completedDate);
    console.log("📦 latestUpdate:", latestUpdate);

    const firstDate = new Date(firstUpload);
    if (isNaN(firstDate)) return ""; // ⛔ skip if invalid

    let endDate;

    if (completedDate && !isNaN(new Date(completedDate))) {
      endDate = new Date(completedDate);
      console.log("✅ Using completedDate as end:", endDate.toISOString());
    } else if (latestUpdate && !isNaN(new Date(latestUpdate))) {
      endDate = new Date(latestUpdate);
      console.log("🕓 Using latestUpdate as end:", endDate.toISOString());
    } else {
      const bestDates = progressData
        .filter((item) => item.best_date && !isNaN(new Date(item.best_date)))
        .map((item) => new Date(item.best_date));

      endDate = bestDates.length > 0
        ? new Date(Math.max(...bestDates.map((d) => d.getTime())))
        : null;

      if (endDate && !isNaN(endDate)) {
        console.log("🛟 Fallback end date from progressData:", endDate.toISOString());
      } else {
        return ""; // ✅ fail-safe
      }
    }

    const durationInMs = endDate - firstDate;
    const durationInMonths = durationInMs / (1000 * 60 * 60 * 24 * 30.44);
    const durationInYears = durationInMs / (1000 * 60 * 60 * 24 * 365.25);

    console.log("📏 Duration in months:", durationInMonths.toFixed(2));
    console.log("📏 Duration in years:", durationInYears.toFixed(2));

    // Display rules
    if (durationInMonths < 1) return ""; // Less than 1 month → don't show

    if (durationInMonths < 12) {
      return `${Math.floor(durationInMonths)} mo`;
    }

    const years = Math.floor(durationInYears);
    const months = Math.floor((durationInYears - years) * 12);

    return `${years} yr${years > 1 ? "s" : ""}${months > 0 ? ` ${months} mo` : ""}`;
  };


  if (loading) {
    console.warn("⚠️ Profile is still loading, but rendering the UI anyway.");
  }

  const handleFavoriteClick = async () => {
    if (!isAuthenticated || !user?.username) {
      alert("Please log in to manage favorites.");
      return;
    }
    if (!displayedUser?.username) {
      return;
    }

    const displayedUsername = displayedUser.username;
    const isCurrentlyFavorited = !!favorites[displayedUsername];

    // Optimistically update the UI
    const updatedFavoritesState = {
      ...favorites,
      [displayedUsername]: !isCurrentlyFavorited,
    };
    setFavorites(updatedFavoritesState);

    // Prepare data for DynamoDB
    const favoritesArray = Object.keys(updatedFavoritesState).filter(
      (key) => updatedFavoritesState[key]
    );

    try {
      // Save the updated list to the logged-in user's profile
      await saveUserProfileToDB(user.username, { favorites: favoritesArray });
    } catch (error) {
      console.error("Failed to save favorites:", error);
      // Revert the change if the save fails
      setFavorites(favorites);
      alert("Could not update favorite status. Please try again.");
    }
  };

  const handleMissingUserModalClose = () => {
    setCustomModal(null);

    // Redirect based on whether a user is signed in
    if (user?.username) {
      navigate(`/progress/${user.username}`);
    } else {
      navigate("/progress");
    }
  };

  return (
    <Container className="profilepage-card-container compact">
      <div className="profilepage-card-header">
        {/* Top-right Settings Icon */}
        {isAuthenticated && (
          <div className="profilepage-settings-button">
            <FaCog
              className="profilepage-settings-icon"
              onClick={() => navigate('/settings')}
            />
          </div>
        )}

        <div className="profilepage-header-controls">
          {isAuthenticated && (
            <button className="profilepage-header-button" onClick={handleMeButtonClick}>
              Me
            </button>
          )}
          <input
            type="text"
            placeholder="Search User"
            value={searchInput}
            onChange={(e) => {
              const value = e.target.value;
              setSearchInput(value);

              if (value.length > 0) {
                setSuggestions(
                  allUsers.filter((u) => {
                    const haystack = (u.displayName || u.username || "").toLowerCase();
                    return haystack.includes(value.toLowerCase());
                  })
                );
              } else {
                setSuggestions([]);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                // Move down, but stop at the last item
                setSelectedIndex((prevIndex) => (prevIndex < suggestions.length - 1 ? prevIndex + 1 : prevIndex));
              } else if (e.key === "ArrowUp") {
                // Move up, but stop at the first item
                setSelectedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
              } else if (e.key === "Enter") {
                if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
                  const selectedUser = suggestions[selectedIndex];
                  const label = selectedUser.displayName || selectedUser.username;

                  setSearchTerm(selectedUser.username);
                  setSearchInput(label);
                  setSuggestions([]);
                  sessionStorage.setItem(
                    "selectedUser",
                    JSON.stringify({ username: selectedUser.username })
                  );
                } else {
                  // fallback: exact match by username or displayName
                  const match = allUsers.find(
                    (u) =>
                      u.username === searchInput || u.displayName === searchInput
                  );
                  if (match) {
                    const label = match.displayName || match.username;
                    setSearchTerm(match.username);
                    setSearchInput(label);
                    sessionStorage.setItem(
                      "selectedUser",
                      JSON.stringify({ username: match.username })
                    );
                  } else {
                    alert("❌ User not found! Try again.");
                  }
                }
              }
            }}
            className="profilepage-search-input"
          />
          {suggestions.length > 0 && (
            <ul className="dropdown-list">
              {suggestions.map((u, index) => (
                <li
                  key={u.username}
                  className={index === selectedIndex ? "dropdown-item selected" : "dropdown-item"}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const label = u.displayName || u.username;
                    setSearchTerm(u.username);           // always navigate by canonical username
                    setSearchInput(label);               // show pretty displayName in the input
                    setSuggestions([]);
                    sessionStorage.setItem(
                      "selectedUser",
                      JSON.stringify({ username: u.username })
                    );
                    if (preselectedUsername !== u.username) {
                      navigate(`/progress/${u.username}`);
                    }
                  }}
                >
                  {u.displayName || u.username}
                </li>
              ))}
            </ul>
          )}
          <button
            className="profilepage-header-button"
            onClick={() => navigate('/community')}
          >
            All
          </button>
        </div>
        {/* Add this new container in its place */}
        <div className="profilepage-top-right-icons">
          {/* The existing heart icon is now inside the new container */}
          <div
            className={`profilepage-heart-icon ${favorites[displayedUser?.username] ? "favorited" : "unfavorited"}`}
            onClick={handleFavoriteClick}
          >
            ❤
          </div>
        </div>
      </div>
      <div className="profilepage-card-content">
        <div className="profilepage-avatar-badge-container">
          <div className="profilepage-avatar-container">
            {displayedUser && (
              <div className="profilepage-avatar-label">
                {isAuthenticated && displayedUser?.username === user?.username ? (
                  <AvatarEditor
                    key={displayedUser?.avatar || 'no-avatar'}  // 🔁 remount when the URL changes
                    username={user.username}
                    initialAvatarUrl={resolveAvatarUrl(displayedUser?.avatar)}
                    onAvatarChange={async () => {
                      await loadUserProfile();                   // you already do this
                    }}
                  />
                ) : (
                  <AvatarImg
                    src={resolveAvatarUrl(displayedUser?.avatar)}
                    alt="Avatar"
                    className="profilepage-avatar-image"
                  />
                )}
              </div>
            )}
            <div className="profilepage-handle-container">
              {displayedUser?.displayName || displayedUser?.username || "Loading..."}
            </div>
          </div>

          <div className="profilepage-details-container">
            <div className="profilepage-badge-container">
              {dynamicBadges
                .filter((badge) => badge.level !== badgeLevel) // Exclude the current badge level
                .slice(0, 6) // Limit to the first 6 badges
                .map((badge, index) => (
                  <OverlayTrigger
                    key={index}
                    placement="top"
                    overlay={<Tooltip>{badge.level}</Tooltip>}
                  >
                    <img
                      src={badge.image}
                      alt={badge.level}
                      className={`profilepage-badge-image ${badge.unlocked ? '' : 'profilepage-locked-badge'}`}
                    />
                  </OverlayTrigger>
                ))}
            </div>
          </div>

          <div className="profilepage-current-badge-container">
            <img
              src={currentBadge}
              alt={badgeLevel}
              className="profilepage-current-badge-image profilepage-no-circle"
            />
            <div className="profilepage-current-badge-label">{badgeLevel}</div>
          </div>
        </div>

        <div className="profilepage-journey-completion-container">
          <div className="profilepage-journey-completion-header">
            <div className="profilepage-journey-completion-label">Journey</div>
            {/*
            {displayedUser?.first_upload && (
              <div className="profilepage-journey-completion-duration">
                {calculateDurationDisplay(
                  displayedUser.first_upload,
                  displayedUser.completed_date,
                  displayedUser.latest_update,
                  displayedUser.progressData
                )}
              </div>
            )}
            */}
            <div className="profilepage-journey-completion-percentage">{journeyCompletion}%</div>
          </div>
          <div className="profilepage-journey-completion-bar">
            <div
              className="profilepage-journey-completion-fill"
              style={{
                width: `${journeyCompletion}%`,
                backgroundColor: getProgressBarColor(journeyCompletion),
              }}
            ></div>
          </div>
        </div>

        <div className="profilepage-progress-card-container">
          {displayedUser && (
            <CardHSPUProgressProfile
              data_user={[displayedUser]}
              isOwnProfile={isAuthenticated && user.username === displayedUser?.username}
              onUpdateProgress={handleForceReload}
              journeyCompletion={journeyCompletion}
              externalUser={externalUser}
              externalLevelToOpen={externalLevelToOpen}
              externalBestUrl={
                externalBestUrl ??
                displayedUser.progressData?.find(p => p.progress_code === externalLevelToOpen)?.best_url ??
                null
              }
            />
          )}
        </div>
      </div>

      {customModal && (
        <div className={styles.videoPopup}>
          <div className={styles.videoCloseButton} onClick={handleMissingUserModalClose}>
            &times;
          </div>
          <div className={styles.comingSoonMessage}>{customModal.title}</div>
          <img src={logo} alt="thenics4all Logo" className={styles.logo} />
          <div className={styles.comingSoonMessageSubtitle}>
            {customModal.message}
          </div>

          {customModal.related && (() => {
            const { lower, higher } = customModal.related;
            const uniqueVideos = [];

            if (lower?.url) uniqueVideos.push(lower);
            if (higher?.url && (!lower || higher.url !== lower.url)) {
              uniqueVideos.push(higher);
            }

            return (
              <div style={{ marginTop: "20px", textAlign: "center" }}>
                {uniqueVideos.map((video, idx) => (
                  <div key={idx} style={{ marginBottom: "20px" }}>
                    <video
                      src={video.url}
                      controls
                      className={styles.modalVideoSlide}
                    />
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {showCongrats && (
        <ModalCongrats show={showCongrats} onHide={handleCloseCongrats} />
      )}

    </Container>
  );
}

export default ProfilePage;
